import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@store/authStore';
import { getDefaultRouteForRole } from '@config/roles';
import { hasRoutePermission } from '@config/routes';
import { ShimmerLoading } from '@components/common/ShimmerLoading/ShimmerLoading';

interface ProtectedRouteProps {
    children: React.ReactNode;
    allowedRoles?: string[]; // Optional: specify allowed roles for this route
}

/**
 * ProtectedRoute Component
 * Provides both authentication and authorization checks
 *
 * Features:
 * - Checks if user is authenticated
 * - Checks if user has permission to access the route
 * - Redirects unauthorized users to /unauthorized page
 * - Uses role-based permissions from config/roles.ts
 */
const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, allowedRoles }) => {
    const { isAuthenticated, isLoading, user } = useAuth();
    const location = useLocation();
    const [isChecking, setIsChecking] = useState(true);

    console.log('ProtectedRoute: Component rendered for path:', location.pathname);
    console.log('ProtectedRoute: Auth state:', { isAuthenticated, isLoading, user: !!user });

    // Check localStorage directly for immediate authentication check
    const token = localStorage.getItem('authToken');
    const userJson = localStorage.getItem('user');
    const isAuthenticatedFlag = localStorage.getItem('isAuthenticated');
    const hasAuthData = token && userJson && isAuthenticatedFlag === 'true';

    console.log('ProtectedRoute: localStorage check:', { token: !!token, userJson: !!userJson, isAuthenticated: isAuthenticatedFlag });
    console.log('ProtectedRoute: hasAuthData:', hasAuthData);

    // More efficient auth checking
    useEffect(() => {
        // If we have auth data from localStorage or auth store, immediately stop checking
        if (hasAuthData || isAuthenticated) {
            console.log('ProtectedRoute: Authenticated - stopping check');
            setIsChecking(false);
            return;
        }

        // If we're not loading, stop checking after a brief delay
        if (!isLoading) {
            const timeoutId = setTimeout(() => {
                console.log('ProtectedRoute: Not loading and no auth - stopping check');
                setIsChecking(false);
            }, 100);
            return () => clearTimeout(timeoutId);
        }

        console.log('ProtectedRoute: Still loading, will check again...');
    }, [hasAuthData, isAuthenticated, isLoading]);

    // STATE 1: Loading - Show shimmer loading
    if (isLoading || isChecking) {
        console.log('ProtectedRoute: STATE 1 - Loading, showing shimmer');
        return <ShimmerLoading type="dashboard" fullScreen />;
    }

    // STATE 2: Not Authenticated - Redirect to login
    if (!hasAuthData && !isAuthenticated) {
        console.log('ProtectedRoute: STATE 2 - Not authenticated, redirecting to login');
        return <Navigate to="/login" state={{ from: location }} replace />;
    }

    // STATE 3: Check Authorization (Role-based access control)
    const currentUser = user || (userJson ? JSON.parse(userJson) : null);
    const userRole = currentUser?.role;

    console.log('ProtectedRoute: Current user role:', userRole);

    if (!userRole) {
        console.log('ProtectedRoute: No user role found, redirecting to login');
        return <Navigate to="/login" state={{ from: location }} replace />;
    }

    // Check if user has permission to access this route
    let hasPermission = false;

    if (allowedRoles && allowedRoles.length > 0) {
        // Use explicitly provided allowed roles
        hasPermission = allowedRoles.includes(userRole);
        console.log('ProtectedRoute: Using explicit allowedRoles, permission:', hasPermission);
    } else {
        // Use RoutePermissions config
        hasPermission = hasRoutePermission(userRole, location.pathname);
        console.log('ProtectedRoute: Using RoutePermissions config, permission:', hasPermission);
    }

    // STATE 4: Authorized - Show protected content
    if (hasPermission) {
        console.log('ProtectedRoute: STATE 4 - Authorized, showing protected content');
        return <>{children}</>;
    }

    // STATE 5: Authenticated but not authorized - Redirect to unauthorized page
    console.log('ProtectedRoute: STATE 5 - Not authorized, redirecting to unauthorized');
    const defaultRoute = getDefaultRouteForRole(userRole);
    return (
        <Navigate
            to="/unauthorized"
            state={{ from: location, defaultRoute }}
            replace
        />
    );
};

export default ProtectedRoute;
